import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { ArrowRight, Clock3, Keyboard } from 'lucide-react'
import { testsApi } from '@/api/tests'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { EmptyState } from '@/components/EmptyState'
import { MultipleChoiceQuestion } from '@/components/tests/MultipleChoiceQuestion'
import { TypingQuestion } from '@/components/tests/TypingQuestion'
import { FillBlankQuestion } from '@/components/tests/FillBlankQuestion'
import { MatchingQuestion } from '@/components/tests/MatchingQuestion'
import { TestResultPage } from '@/pages/tests/TestResultPage'
import type { GeneratedTest, QuestionType, TestAnswerSubmission, TestQuestion, TestResult } from '@/types/test'

type SessionPhase = 'missing' | 'loading' | 'active' | 'submitting' | 'result'

interface LocationState {
  test?: GeneratedTest
}

const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  MULTIPLE_CHOICE: 'Multiple choice',
  TYPING: 'Typing',
  FILL_BLANK: 'Fill in blank',
  MATCHING: 'Matching',
}

export function TestSessionPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as LocationState | null

  const [phase, setPhase] = useState<SessionPhase>(() => {
    if (state?.test) return 'active'
    if (id) return 'loading'
    return 'missing'
  })
  const [test, setTest] = useState<GeneratedTest | null>(state?.test ?? null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [matchingAnswers, setMatchingAnswers] = useState<Record<string, Record<string, string>>>({})
  const [answerTimes, setAnswerTimes] = useState<Record<string, number>>({})
  const [questionStartedAt, setQuestionStartedAt] = useState(Date.now())
  const [elapsedMs, setElapsedMs] = useState(0)
  const [result, setResult] = useState<TestResult | null>(null)

  const submitMutation = useMutation({
    mutationFn: (payload: { answers: TestAnswerSubmission[]; durationMs: number }) =>
      testsApi.submitTest(id!, payload),
    onSuccess: (data) => {
      setResult(data)
      setPhase('result')
    },
  })

  useEffect(() => {
    if (state?.test || !id) {
      return
    }

    let cancelled = false
    testsApi
      .getTest(id)
      .then((data) => {
        if (!cancelled) {
          setTest(data)
          setPhase('active')
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPhase('missing')
        }
      })

    return () => {
      cancelled = true
    }
  }, [id, state?.test])

  useEffect(() => {
    if (phase !== 'active') {
      return
    }
    const timer = window.setInterval(() => {
      setElapsedMs(Date.now() - questionStartedAt)
    }, 250)
    return () => window.clearInterval(timer)
  }, [phase, questionStartedAt])

  const currentQuestion = test?.questions[currentIndex]
  const totalQuestions = test?.questions.length ?? 0
  const progressPercent = totalQuestions === 0 ? 0 : (currentIndex / totalQuestions) * 100

  const currentAnswer = useMemo(() => {
    if (!currentQuestion) {
      return ''
    }
    if (currentQuestion.type === 'MATCHING') {
      const pairs = currentQuestion.pairs ?? []
      const matches = matchingAnswers[currentQuestion.id] ?? {}
      return JSON.stringify(
        pairs
          .map((pair) => ({ word: pair.word, translation: matches[pair.word] ?? '' }))
          .filter((pair) => pair.translation)
      )
    }
    return answers[currentQuestion.id] ?? ''
  }, [answers, currentQuestion, matchingAnswers])

  const canProceed = useMemo(() => {
    if (!currentQuestion) {
      return false
    }
    if (currentQuestion.type === 'MATCHING') {
      const pairs = currentQuestion.pairs ?? []
      const matches = matchingAnswers[currentQuestion.id] ?? {}
      return pairs.every((pair) => Boolean(matches[pair.word]))
    }
    return currentAnswer.trim().length > 0
  }, [currentAnswer, currentQuestion, matchingAnswers])

  const handleAnswerChange = useCallback((questionId: string, value: string) => {
    setAnswers((current) => ({ ...current, [questionId]: value }))
  }, [])

  const handleMatchingChange = useCallback((questionId: string, word: string, translation: string) => {
    setMatchingAnswers((current) => ({
      ...current,
      [questionId]: {
        ...(current[questionId] ?? {}),
        [word]: translation,
      },
    }))
  }, [])

  const buildSubmissionPayload = useCallback(
    (finalQuestionTimeMs?: number) => {
      if (!test || !currentQuestion) {
        return { answers: [], durationMs: 0 }
      }

      const latestAnswerTimes = {
        ...answerTimes,
        ...(finalQuestionTimeMs ? { [currentQuestion.id]: finalQuestionTimeMs } : {}),
      }

      const submissions: TestAnswerSubmission[] = test.questions.map((question) => {
        let answer = answers[question.id] ?? ''

        if (question.type === 'MATCHING') {
          const pairs = question.pairs ?? []
          const matches = matchingAnswers[question.id] ?? {}
          answer = JSON.stringify(pairs.map((pair) => ({ word: pair.word, translation: matches[pair.word] ?? '' })))
        }

        return {
          questionId: question.id,
          answer,
          answerTimeMs: latestAnswerTimes[question.id] ?? 1000,
        }
      })

      return {
        answers: submissions,
        durationMs: Object.values(latestAnswerTimes).reduce((sum, ms) => sum + ms, 0),
      }
    },
    [answerTimes, answers, currentQuestion, matchingAnswers, test]
  )

  const handleNext = useCallback(() => {
    if (!test || !currentQuestion || !canProceed || submitMutation.isPending) {
      return
    }

    const answerTimeMs = Date.now() - questionStartedAt
    setAnswerTimes((current) => ({ ...current, [currentQuestion.id]: answerTimeMs }))

    if (currentIndex < test.questions.length - 1) {
      setCurrentIndex((value) => value + 1)
      setQuestionStartedAt(Date.now())
      setElapsedMs(0)
      return
    }

    setPhase('submitting')
    submitMutation.mutate(buildSubmissionPayload(answerTimeMs))
  }, [
    buildSubmissionPayload,
    canProceed,
    currentIndex,
    currentQuestion,
    questionStartedAt,
    submitMutation,
    test,
  ])

  useEffect(() => {
    if (phase !== 'active' || !currentQuestion) {
      return
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' || event.shiftKey || !canProceed || submitMutation.isPending) {
        return
      }

      const target = event.target
      if (target instanceof HTMLTextAreaElement) {
        return
      }
      if (target instanceof HTMLSelectElement) {
        return
      }

      event.preventDefault()
      handleNext()
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [canProceed, currentQuestion, handleNext, phase, submitMutation.isPending])

  const renderQuestion = (question: TestQuestion) => {
    switch (question.type) {
      case 'MULTIPLE_CHOICE':
        return (
          <MultipleChoiceQuestion
            questionText={question.questionText}
            senseGloss={question.senseGloss}
            options={question.options ?? []}
            selected={answers[question.id]}
            onSelect={(value) => handleAnswerChange(question.id, value)}
          />
        )
      case 'TYPING':
        return (
          <TypingQuestion
            questionText={question.questionText}
            senseGloss={question.senseGloss}
            value={answers[question.id] ?? ''}
            onChange={(value) => handleAnswerChange(question.id, value)}
            onSubmit={handleNext}
          />
        )
      case 'FILL_BLANK':
        return (
          <FillBlankQuestion
            questionText={question.questionText}
            options={question.options}
            value={answers[question.id] ?? ''}
            onChange={(value) => handleAnswerChange(question.id, value)}
          />
        )
      case 'MATCHING':
        return (
          <MatchingQuestion
            pairs={question.pairs ?? []}
            matches={matchingAnswers[question.id] ?? {}}
            onMatch={(word, translation) => handleMatchingChange(question.id, word, translation)}
          />
        )
      default:
        return null
    }
  }

  if (phase === 'missing') {
    return (
      <EmptyState
        title="Test session not found"
        description="Start a new test from the Tests page."
        action={<Button onClick={() => navigate('/tests')}>Back to tests</Button>}
      />
    )
  }

  if (phase === 'loading' || !test) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  if (phase === 'result' && result) {
    return (
      <div className="mx-auto max-w-3xl">
        <TestResultPage result={result} onRetake={() => navigate('/tests')} />
      </div>
    )
  }

  if (phase === 'submitting' || !test || !currentQuestion) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  const isLastQuestion = currentIndex >= totalQuestions - 1

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <div className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-primary">Vocabulary test</p>
            <h1 className="text-2xl font-bold text-foreground">
              Question {currentIndex + 1}
              <span className="font-normal text-muted-foreground"> / {totalQuestions}</span>
            </h1>
          </div>
          <div className="flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground">
            <Clock3 className="h-3.5 w-3.5" />
            <span className="tabular-nums">{(elapsedMs / 1000).toFixed(1)}s</span>
          </div>
        </div>

        <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
          <div
            className="h-full rounded-full bg-primary transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="flex items-center justify-between border-b border-border bg-secondary/20 px-5 py-3">
          <span className="rounded-md bg-secondary px-2.5 py-1 text-xs font-medium text-muted-foreground">
            {QUESTION_TYPE_LABELS[currentQuestion.type]}
          </span>
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Keyboard className="h-3.5 w-3.5" />
            Enter to continue
          </span>
        </div>

        <div className="p-6 sm:p-8">{renderQuestion(currentQuestion)}</div>

        <div className="flex items-center justify-between border-t border-border bg-secondary/10 px-5 py-4">
          <p className="text-xs text-muted-foreground">
            {canProceed ? 'Ready to continue' : 'Answer to continue'}
          </p>
          <Button
            onClick={handleNext}
            disabled={!canProceed || submitMutation.isPending}
            className="w-auto gap-2"
          >
            {isLastQuestion ? 'Submit test' : 'Next'}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </Card>
    </div>
  )
}
